import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Upload, Loader2, X } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { AsyncSearchableSelect } from '../ui/async-searchable-select';
import { SearchableSelect } from '../ui/searchable-select';
import { toast } from 'sonner';
import { uploadsApi, directoryApi } from '../../services/api';

const mapPortToOption = (p) => ({
  value: p.code,
  label: p.nameAndLocode || `${p.name} (${p.code})`,
  country: p.country,
});
const BILL_OF_LADING_TYPE_OPTIONS = [
  { value: 'MBL', label: 'MBL (Master Bill of Lading)' },
  { value: 'HBL', label: 'HBL (House Bill of Lading)' },
];
const BILL_OF_LADING_FORMAT_OPTIONS = [
  { value: 'Original', label: 'Original' },
  { value: 'SWBL', label: 'Sea Waybill' },
  { value: 'TELEX', label: 'Telex Release' },
];
const OOG_GAUGE_OPTIONS = [
  { value: 'in', label: 'In-Gauge' },
  { value: 'out', label: 'Out-Gauge' },
];
const DIMENSION_UNIT_OPTIONS = [
  { value: 'mm', label: 'mm' },
  { value: 'cm', label: 'cm' },
  { value: 'inch', label: 'Inch' },
  { value: 'm', label: 'Meter' },
  { value: 'ft', label: 'ft' },
];
const HAZARDOUS_CARGO_OPTIONS = [
  { value: 'no', label: 'Non-Hazardous' },
  { value: 'yes', label: 'Hazardous' },
];
const HAZ_CLASS_OPTIONS = [
  'Class 1 - Explosives',
  'Class 2 - Gases',
  'Class 3 - Flammable Liquids',
  'Class 4 - Flammable Solids',
  'Class 5 - Oxidizing Substances',
  'Class 6 - Toxic Substances',
  'Class 7 - Radioactive Material',
  'Class 8 - Corrosives',
  'Class 9 - Miscellaneous'
];

const MAX_ATTACHMENTS_PER_CONTAINER = 5;
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_MSDS_FILES = 5;
const MAX_MSDS_FILE_SIZE_MB = 5;
const MAX_MSDS_FILE_SIZE_BYTES = MAX_MSDS_FILE_SIZE_MB * 1024 * 1024;

const DEFAULT_OCEAN_FORM = {
  scope: [],
  mode: 'Ocean Freight',
  por: '', porCountry: '', porName: '',
  pol: '', polCountry: '', polName: '',
  pod: '', podCountry: '', podName: '',
  fpod: '', fpodCountry: '', fpodName: '',
  incoterm: '',
  blType: '', blFormat: '',
  polFreeTime: '', podFreeTime: '',
  containers: {
    '20GP': 0, '40GP': 0, '40HC': 0,
    '20OT': 0, '40OT': 0, '40OTHC': 0,
    '20FR': 0, '40FR': 0, '40FB': 0
  },
  containerWeights: {
    '20GP': '',
    '40GP': '',
    '40HC': ''
  },
  oogContainers: [],
  reefer: { '20RF': 0, '40RF': 0 },
  tempRange20: '', tempRange40: '',
  commodity: '',
  isHaz: false,
  hazClass: '',
  msdsFiles: [],
  paymentTerms: {
    originCharges: { payer: 'Shipper', currency: 'USD' },
    oceanFreight: { payer: 'Shipper', currency: 'USD' },
    destCarrierCharges: { payer: 'Consignee', currency: 'USD' },
    destDeliveryCharges: { payer: 'Consignee', currency: 'USD' }
  },
  specialRequirements: ''
};

const DEFAULT_SCOPE_OPTIONS = ['Origin Inland (FCL)', 'Origin Customs (FCL)', 'Origin Port Terminal (FCL)', 'Ocean Freight (FCL)', 'Destination Port (FCL)', 'Destination Customs (FCL)', 'Destination Inland (FCL)'];

const OceanFreightForm = ({ onSubmit, onCancel, oceanPorts = [], porFpodPorts = [], incoterms = [], commodities = [], initialData = null, formId = null, scopeOptions: scopeOptionsProp, onFormDataChange, validationErrors = {}, clearValidationError }) => {
  const [formData, setFormData] = useState(() => {
    if (initialData && typeof initialData === 'object') {
      const normalizedIncoterm = Array.isArray(initialData.incoterm)
        ? (initialData.incoterm[0] || '')
        : (initialData.incoterm || '');
      return {
        ...DEFAULT_OCEAN_FORM,
        ...initialData,
        incoterm: normalizedIncoterm,
        msdsFiles: Array.isArray(initialData.msdsFiles) ? initialData.msdsFiles : [],
        containers: { ...DEFAULT_OCEAN_FORM.containers, ...(initialData.containers || {}) },
        containerWeights: { ...DEFAULT_OCEAN_FORM.containerWeights, ...(initialData.containerWeights || {}) },
        reefer: { ...DEFAULT_OCEAN_FORM.reefer, ...(initialData.reefer || {}) },
        paymentTerms: { ...DEFAULT_OCEAN_FORM.paymentTerms, ...(initialData.paymentTerms || {}) }
      };
    }
    return { ...DEFAULT_OCEAN_FORM };
  });
  const [portLabels, setPortLabels] = useState({ por: '', pol: '', pod: '', fpod: '' });
  const scopeOptions = scopeOptionsProp && scopeOptionsProp.length > 0 ? scopeOptionsProp : DEFAULT_SCOPE_OPTIONS;
  const commodityOptions = commodities.map((comm) => {
    const value = typeof comm === 'object' ? comm.name || String(comm) : comm;
    return { value, label: value };
  });
  const incotermOptions = incoterms.map((term) => {
    const value = typeof term === 'object' ? term.code : term;
    const label = typeof term === 'object' ? `${term.code} - ${term.name}` : term;
    return { value, label };
  });
  const hazClassOptions = HAZ_CLASS_OPTIONS.map((hc) => ({ value: hc, label: hc }));

  // Fetch display labels for port codes when we have initialData (e.g. editing a quote)
  useEffect(() => {
    const codes = { por: formData.por, pol: formData.pol, pod: formData.pod, fpod: formData.fpod };
    const fetchLabel = async (field, code, apiByCode) => {
      if (!code) return;
      try {
        const res = await apiByCode(code);
        const item = res.data;
        const label = item?.nameAndLocode || (item?.name && item?.code ? `${item.name} (${item.code})` : code);
        setPortLabels((prev) => ({ ...prev, [field]: label }));
      } catch (_) {
        setPortLabels((prev) => ({ ...prev, [field]: code }));
      }
    };
    if (codes.por) fetchLabel('por', codes.por, directoryApi.getPorFpodByCode);
    if (codes.pol) fetchLabel('pol', codes.pol, directoryApi.getPortByCode);
    if (codes.pod) fetchLabel('pod', codes.pod, directoryApi.getPortByCode);
    if (codes.fpod) fetchLabel('fpod', codes.fpod, directoryApi.getPorFpodByCode);
  }, [formData.por, formData.pol, formData.pod, formData.fpod]);

  useEffect(() => {
    onFormDataChange?.(formData);
  }, [formData, onFormDataChange]);

  const hazClasses = HAZ_CLASS_OPTIONS;

  const oogContainerTypes = ['45HC', '20OT', '40OT', '40OTHC', '20FR', '40FR', '40FB'];

  const handleScopeChange = (scope) => {
    const newScopes = formData.scope.includes(scope)
      ? formData.scope.filter(s => s !== scope)
      : [...formData.scope, scope];
    const next = { ...formData, scope: newScopes };
    setFormData(next);
    onFormDataChange?.({ scope: newScopes });
  };

  // Counter for unique IDs
  const containerCounter = React.useRef(0);

  // Add individual OOG container with its own dimensions
  const addOOGContainer = (containerType) => {
    containerCounter.current += 1;
    const newContainer = {
      id: `oog-${containerCounter.current}`,
      type: containerType,
      gauge: 'in',
      length: '',
      width: '',
      height: '',
      unit: 'mm',
      weight: '',
      cargoDescription: '',
      attachments: []
    };
    setFormData({
      ...formData,
      oogContainers: [...formData.oogContainers, newContainer]
    });
    toast.success(`Added ${containerType} container`);
  };

  const updateOOGContainer = (id, field, value) => {
    setFormData({
      ...formData,
      oogContainers: formData.oogContainers.map(c =>
        c.id === id ? { ...c, [field]: value } : c
      )
    });
  };

  const removeOOGContainer = (id) => {
    setFormData({
      ...formData,
      oogContainers: formData.oogContainers.filter(c => c.id !== id)
    });
    toast.info('Container removed');
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate no negative values
    const containers = formData.containers;
    const reefer = formData.reefer;

    for (const [type, qty] of Object.entries(containers)) {
      if (qty < 0) {
        toast.error(`Container ${type} quantity cannot be negative`);
        return;
      }
    }

    for (const [type, qty] of Object.entries(reefer)) {
      if (qty < 0) {
        toast.error(`Reefer ${type} quantity cannot be negative`);
        return;
      }
    }

    // Validate OOG container dimensions
    for (const container of formData.oogContainers) {
      if (container.length < 0 || container.width < 0 || container.height < 0 || container.weight < 0) {
        toast.error('OOG container dimensions and weight cannot be negative');
        return;
      }
    }

    onSubmit(formData);
  };

  // Count OOG containers by type
  const getOOGCountByType = (type) => {
    return formData.oogContainers.filter(c => c.type === type).length;
  };

  // 1-based count for this container among same type (for "Count-1" label)
  const getOOGCountForContainer = (container) => {
    const sameType = formData.oogContainers.filter(c => c.type === container.type);
    return sameType.findIndex(c => c.id === container.id) + 1;
  };

  const [uploadingContainerId, setUploadingContainerId] = useState(null);
  const [uploadingMsds, setUploadingMsds] = useState(false);
  const msdsInputRef = useRef(null);

  const handleContainerFileSelect = async (containerId, files) => {
    const container = formData.oogContainers.find(c => c.id === containerId);
    if (!container) return;
    const current = (container.attachments || []).length;
    const fileList = Array.from(files || []);
    if (current + fileList.length > MAX_ATTACHMENTS_PER_CONTAINER) {
      toast.error(`Maximum ${MAX_ATTACHMENTS_PER_CONTAINER} files per container. You have ${current} and tried to add ${fileList.length}.`);
      return;
    }
    for (const file of fileList) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`File "${file.name}" exceeds ${MAX_FILE_SIZE_MB}MB.`);
        return;
      }
    }
    setUploadingContainerId(containerId);
    try {
      const uploaded = [];
      for (const file of fileList) {
        const res = await uploadsApi.uploadFile(file, 'quote_document');
        const url = res.data?.file?.url || res.data?.file_url;
        const name = res.data?.file?.original_filename || res.data?.original_filename || file.name;
        if (url) uploaded.push({ url, original_filename: name });
      }
      if (uploaded.length > 0) {
        const nextAttachments = [...(container.attachments || []), ...uploaded];
        updateOOGContainer(containerId, 'attachments', nextAttachments);
        toast.success(`${uploaded.length} file(s) attached.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload file(s).');
    } finally {
      setUploadingContainerId(null);
    }
  };

  const removeContainerAttachment = (containerId, attIndex) => {
    const container = formData.oogContainers.find(c => c.id === containerId);
    if (!container || !container.attachments) return;
    const next = container.attachments.filter((_, i) => i !== attIndex);
    updateOOGContainer(containerId, 'attachments', next);
  };

  const handleMsdsUpload = async (event) => {
    const fileList = Array.from(event.target.files || []);
    if (!fileList.length) return;

    const currentFiles = Array.isArray(formData.msdsFiles) ? formData.msdsFiles : [];
    if (currentFiles.length + fileList.length > MAX_MSDS_FILES) {
      toast.error(`Maximum ${MAX_MSDS_FILES} MSDS files allowed.`);
      event.target.value = '';
      return;
    }

    for (const file of fileList) {
      if (file.size > MAX_MSDS_FILE_SIZE_BYTES) {
        toast.error(`File "${file.name}" exceeds ${MAX_MSDS_FILE_SIZE_MB}MB.`);
        event.target.value = '';
        return;
      }
    }

    setUploadingMsds(true);
    try {
      const uploaded = [];
      for (const file of fileList) {
        const res = await uploadsApi.uploadFile(file, 'quote_document');
        const url = res?.data?.file?.url || res?.data?.file_url || res?.data?.url;
        const name = res?.data?.file?.original_filename || res?.data?.original_filename || file.name;
        const size = res?.data?.file?.size || file.size;
        if (url) {
          uploaded.push({ name, url, size });
        }
      }

      if (uploaded.length > 0) {
        setFormData((prev) => ({
          ...prev,
          msdsFiles: [...(prev.msdsFiles || []), ...uploaded]
        }));
        toast.success(`${uploaded.length} MSDS file(s) uploaded.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload MSDS file(s).');
    } finally {
      setUploadingMsds(false);
      event.target.value = '';
    }
  };

  const removeMsdsFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      msdsFiles: (prev.msdsFiles || []).filter((_, i) => i !== index)
    }));
  };

  return (
    <form id={formId} onSubmit={handleSubmit} className="space-y-6" noValidate>
      {/* Scope Selection */}
      <div>
        <Label>Scope (Select all that apply)</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
          {scopeOptions.map((scope) => (
            <label key={scope} className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={formData.scope.includes(scope)}
                onChange={() => {
                  handleScopeChange(scope);
                  clearValidationError?.('scope');
                }}
                className="rounded"
              />
              <span className="text-sm">{scope}</span>
            </label>
          ))}
        </div>
        {validationErrors.scope && <p className="mt-1 text-sm text-red-600">{validationErrors.scope}</p>}
      </div>

      {/* Ports */}
      <div className="border-t pt-4">
        <h3 className="font-semibold text-lg mb-4">Port Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>POR (Place of Receipt)</Label>
            <AsyncSearchableSelect
              fetchOptions={async (q, lim) => {
                const res = await directoryApi.searchPorFpod(q, lim);
                return (res.data || []).map(mapPortToOption);
              }}
              value={formData.por}
              selectedLabel={portLabels.por}
              onChange={(value, item) => {
                setFormData({ ...formData, por: value, porCountry: item?.country || '', porName: item?.label || value });
                setPortLabels((prev) => ({ ...prev, por: item?.label || value }));
                if (value) clearValidationError?.('por');
              }}
              placeholder="Select Port..."
              className="w-full"
              error={Boolean(validationErrors.por)}
            />
            {validationErrors.por && <p className="mt-1 text-sm text-red-600">{validationErrors.por}</p>}
          </div>
          <div>
            <Label>POL (Port of Loading)</Label>
            <AsyncSearchableSelect
              fetchOptions={async (q, lim) => {
                const res = await directoryApi.searchPorts(q, lim);
                return (res.data || []).map(mapPortToOption);
              }}
              value={formData.pol}
              selectedLabel={portLabels.pol}
              onChange={(value, item) => {
                setFormData({ ...formData, pol: value, polCountry: item?.country || '', polName: item?.label || value });
                setPortLabels((prev) => ({ ...prev, pol: item?.label || value }));
                clearValidationError?.('pol');
              }}
              placeholder="Select Port..."
              className="w-full"
              error={Boolean(validationErrors.pol)}
            />
            {validationErrors.pol && <p className="mt-1 text-sm text-red-600">{validationErrors.pol}</p>}
          </div>
          <div>
            <Label>POD (Port of Discharge)</Label>
            <AsyncSearchableSelect
              fetchOptions={async (q, lim) => {
                const res = await directoryApi.searchPorts(q, lim);
                return (res.data || []).map(mapPortToOption);
              }}
              value={formData.pod}
              selectedLabel={portLabels.pod}
              onChange={(value, item) => {
                setFormData({ ...formData, pod: value, podCountry: item?.country || '', podName: item?.label || value });
                setPortLabels((prev) => ({ ...prev, pod: item?.label || value }));
                clearValidationError?.('pod');
              }}
              placeholder="Select Port..."
              className="w-full"
              error={Boolean(validationErrors.pod)}
            />
            {validationErrors.pod && <p className="mt-1 text-sm text-red-600">{validationErrors.pod}</p>}
          </div>
          <div>
            <Label>FPOD (Final Place of Delivery)</Label>
            <AsyncSearchableSelect
              fetchOptions={async (q, lim) => {
                const res = await directoryApi.searchPorFpod(q, lim);
                return (res.data || []).map(mapPortToOption);
              }}
              value={formData.fpod}
              selectedLabel={portLabels.fpod}
              onChange={(value, item) => {
                setFormData({ ...formData, fpod: value, fpodCountry: item?.country || '', fpodName: item?.label || value });
                setPortLabels((prev) => ({ ...prev, fpod: item?.label || value }));
                if (value) clearValidationError?.('fpod');
              }}
              placeholder="Select Port..."
              className="w-full"
              error={Boolean(validationErrors.fpod)}
            />
            {validationErrors.fpod && <p className="mt-1 text-sm text-red-600">{validationErrors.fpod}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div>
            <Label>Incoterm</Label>
            <SearchableSelect
              options={incotermOptions}
              value={formData.incoterm}
              onChange={(value) => {
                setFormData({ ...formData, incoterm: value || '' });
                if (value) clearValidationError?.('incoterm');
              }}
              placeholder="Search or select incoterm"
              allowOther={false}
              error={Boolean(validationErrors.incoterm)}
            />
            {validationErrors.incoterm && <p className="mt-1 text-sm text-red-600">{validationErrors.incoterm}</p>}
          </div>
          <div>
            <Label>POL Free Time (days)</Label>
            <Input
              type="number"
              min="0"
              value={formData.polFreeTime}
              onChange={(e) => setFormData({ ...formData, polFreeTime: Math.max(0, e.target.value) })}
              onInput={(e) => {
                if (e.target.value !== '') clearValidationError?.('polFreeTime');
              }}
              placeholder="e.g., 7"
              className={validationErrors.polFreeTime ? 'border-red-500' : ''}
              required
            />
            {validationErrors.polFreeTime && <p className="mt-1 text-sm text-red-600">{validationErrors.polFreeTime}</p>}
          </div>
          <div>
            <Label>POD Free Time (days)</Label>
            <Input
              type="number"
              min="0"
              value={formData.podFreeTime}
              onChange={(e) => setFormData({ ...formData, podFreeTime: Math.max(0, e.target.value) })}
              onInput={(e) => {
                if (e.target.value !== '') clearValidationError?.('podFreeTime');
              }}
              placeholder="e.g., 7"
              className={validationErrors.podFreeTime ? 'border-red-500' : ''}
              required
            />
            {validationErrors.podFreeTime && <p className="mt-1 text-sm text-red-600">{validationErrors.podFreeTime}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <Label>BL Type</Label>
            <SearchableSelect
              options={BILL_OF_LADING_TYPE_OPTIONS}
              value={formData.blType}
              onChange={(value) => {
                setFormData({ ...formData, blType: value || '' });
                if (value) clearValidationError?.('blType');
              }}
              placeholder="Search or select BL type"
              allowOther={false}
              error={Boolean(validationErrors.blType)}
            />
            {validationErrors.blType && <p className="mt-1 text-sm text-red-600">{validationErrors.blType}</p>}
          </div>
          <div>
            <Label>BL Format</Label>
            <SearchableSelect
              options={BILL_OF_LADING_FORMAT_OPTIONS}
              value={formData.blFormat}
              onChange={(value) => {
                setFormData({ ...formData, blFormat: value || '' });
                if (value) clearValidationError?.('blFormat');
              }}
              placeholder="Search or select BL format"
              allowOther={false}
              error={Boolean(validationErrors.blFormat)}
            />
            {validationErrors.blFormat && <p className="mt-1 text-sm text-red-600">{validationErrors.blFormat}</p>}
          </div>
        </div>
      </div>

      {/* Standard Equipment/Containers */}
      <div className="border-t pt-4">
        <h3 className="font-semibold text-lg mb-4">Standard Containers</h3>
        {validationErrors.containers && <p className="mb-3 text-sm text-red-600">{validationErrors.containers}</p>}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {['20GP', '40GP', '40HC'].map((containerType) => (
            <div key={containerType} className="space-y-2">
              <Label className="text-xs">{containerType}</Label>
              <Input
                type="number"
                min="0"
                value={formData.containers[containerType] === 0 ? '' : formData.containers[containerType]}
                onChange={(e) => {
                  const raw = e.target.value;
                  setFormData({
                    ...formData,
                    containers: { ...formData.containers, [containerType]: raw === '' ? 0 : (parseInt(raw, 10) || 0) }
                  });
                  if (raw !== '' && (parseInt(raw, 10) || 0) > 0) clearValidationError?.('containers');
                }}
                placeholder="Qty"
              />
              <Input
                type="number"
                min="0"
                step="0.01"
                className={validationErrors[`containerWeights.${containerType}`] ? 'border-red-500 focus-visible:ring-red-500' : ''}
                value={formData.containerWeights?.[containerType] ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  setFormData({
                    ...formData,
                    containerWeights: { ...formData.containerWeights, [containerType]: raw }
                  });
                  if (raw !== '' && Number(raw) > 0) clearValidationError?.(`containerWeights.${containerType}`);
                }}
                placeholder="Weight (kg)"
              />
              {validationErrors[`containerWeights.${containerType}`] && (
                <p className="text-sm text-red-600">{validationErrors[`containerWeights.${containerType}`]}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* OOG Containers - Each with individual dimensions */}
      <div className="border-t pt-4">
        <h3 className="font-semibold text-lg mb-4">Special Containers</h3>
        <p className="text-sm text-gray-500 mb-4">Each Special container can have different dimensions. Click &quot;Add&quot; to specify each container individually.</p>

        {/* Add OOG Container Buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          {oogContainerTypes.map(type => (
            <Button
              key={type}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addOOGContainer(type)}
              className="text-xs"
            >
              <Plus size={14} className="mr-1" />
              {type} {getOOGCountByType(type) > 0 && `(${getOOGCountByType(type)})`}
            </Button>
          ))}
        </div>

        {/* OOG Container List */}
        {formData.oogContainers.length > 0 && (
          <div className="space-y-4">
            {formData.oogContainers.map((container, index) => (
              <div key={container.id} className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="font-medium text-amber-900">
                    {container.type} Container
                    <br />
                    <span className="text-sm font-normal text-amber-700">Count-{getOOGCountForContainer(container)}</span>
                  </h5>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeOOGContainer(container.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Gauge Type</Label>
                    <SearchableSelect
                      options={OOG_GAUGE_OPTIONS}
                      className="text-sm h-8"
                      value={container.gauge}
                      onChange={(value) => updateOOGContainer(container.id, 'gauge', value || 'in')}
                      placeholder="Gauge type"
                      allowOther={false}
                    />
                  </div>

                  {container.gauge === 'out' && (
                    <>
                      <div>
                        <Label className="text-xs">Unit</Label>
                        <SearchableSelect
                          options={DIMENSION_UNIT_OPTIONS}
                          className="text-sm h-8"
                          value={container.unit}
                          onChange={(value) => updateOOGContainer(container.id, 'unit', value || 'mm')}
                          placeholder="Unit"
                          allowOther={false}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Length</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Length"
                          value={container.length}
                          onChange={(e) => updateOOGContainer(container.id, 'length', Math.max(0, parseFloat(e.target.value) || 0))}
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Width</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Width"
                          value={container.width}
                          onChange={(e) => updateOOGContainer(container.id, 'width', Math.max(0, parseFloat(e.target.value) || 0))}
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Height</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Height"
                          value={container.height}
                          onChange={(e) => updateOOGContainer(container.id, 'height', Math.max(0, parseFloat(e.target.value) || 0))}
                          className="text-sm"
                        />
                      </div>
                    </>
                  )}

                  <div>
                    <Label className="text-xs">Cargo Weight (kg)</Label>
                    <Input
                      type="number"
                      min="0"
                      placeholder="Weight"
                      value={container.weight}
                      onChange={(e) => updateOOGContainer(container.id, 'weight', Math.max(0, parseFloat(e.target.value) || 0))}
                      className="text-sm"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label className="text-xs">Cargo Description</Label>
                    <Input
                      type="text"
                      placeholder="Brief description of cargo"
                      value={container.cargoDescription}
                      onChange={(e) => updateOOGContainer(container.id, 'cargoDescription', e.target.value)}
                      className="text-sm"
                    />
                  </div>
                </div>

                {/* File upload: max 5 files, 10MB each */}
                <div className="mt-3 pt-3 border-t border-amber-200">
                  <Label className="text-xs block mb-2">Attachments (max {MAX_ATTACHMENTS_PER_CONTAINER} files, {MAX_FILE_SIZE_MB}MB each)</Label>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="cursor-pointer inline-flex">
                      <input
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp,.txt"
                        className="hidden"
                        disabled={(container.attachments || []).length >= MAX_ATTACHMENTS_PER_CONTAINER || uploadingContainerId === container.id}
                        onChange={(e) => {
                          handleContainerFileSelect(container.id, e.target.files);
                          e.target.value = '';
                        }}
                      />
                      <span className="inline-flex items-center justify-center rounded-md text-sm font-medium border border-input bg-background px-3 py-1.5 hover:bg-accent hover:text-accent-foreground">
                        {uploadingContainerId === container.id ? <Loader2 size={14} className="animate-spin mr-1" /> : <Upload size={14} className="mr-1" />}
                        {(container.attachments || []).length >= MAX_ATTACHMENTS_PER_CONTAINER ? 'Max files reached' : 'Add files'}
                      </span>
                    </label>
                    {(container.attachments || []).map((att, ai) => (
                      <span key={ai} className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded border text-xs">
                        {(typeof att === 'object' && att.original_filename) ? att.original_filename : `File ${ai + 1}`}
                        <button type="button" onClick={() => removeContainerAttachment(container.id, ai)} className="text-red-500 hover:text-red-700" aria-label="Remove"><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {formData.oogContainers.length === 0 && (
          <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500 text-sm">
            No Special containers added. Click buttons above to add containers with specific dimensions.
          </div>
        )}
      </div>

      {/* Reefer & Hazardous */}
      <div className="border-t pt-4">
        <h3 className="font-semibold text-lg mb-4">Reefer &amp; Special Cargo</h3>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>20&apos; Reefer</Label>
              <Input
                type="number"
                min="0"
                value={formData.reefer['20RF'] === 0 ? '' : formData.reefer['20RF']}
                onChange={(e) => {
                  const raw = e.target.value;
                setFormData({
                  ...formData,
                  reefer: { ...formData.reefer, '20RF': raw === '' ? 0 : (parseInt(raw, 10) || 0) }
                });
                if (raw !== '' && (parseInt(raw, 10) || 0) > 0) clearValidationError?.('containers');
              }}
                placeholder="0"
              />
            </div>
            <div>
              <Label>Temp Range (20&apos;)</Label>
              <Input
                type="text"
                className={validationErrors.tempRange20 ? 'border-red-500 focus-visible:ring-red-500' : ''}
                value={formData.tempRange20}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({ ...formData, tempRange20: value });
                  if (value.trim()) clearValidationError?.('tempRange20');
                }}
                placeholder="e.g., -25 to +40"
              />
              {validationErrors.tempRange20 && <p className="mt-1 text-sm text-red-600">{validationErrors.tempRange20}</p>}
            </div>
            <div>
              <Label>40&apos; Reefer</Label>
              <Input
                type="number"
                min="0"
                value={formData.reefer['40RF'] === 0 ? '' : formData.reefer['40RF']}
                onChange={(e) => {
                  const raw = e.target.value;
                setFormData({
                  ...formData,
                  reefer: { ...formData.reefer, '40RF': raw === '' ? 0 : (parseInt(raw, 10) || 0) }
                });
                if (raw !== '' && (parseInt(raw, 10) || 0) > 0) clearValidationError?.('containers');
              }}
                placeholder="0"
              />
            </div>
            <div>
              <Label>Temp Range (40&apos;)</Label>
              <Input
                type="text"
                className={validationErrors.tempRange40 ? 'border-red-500 focus-visible:ring-red-500' : ''}
                value={formData.tempRange40}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({ ...formData, tempRange40: value });
                  if (value.trim()) clearValidationError?.('tempRange40');
                }}
                placeholder="e.g., -25 to +25"
              />
              {validationErrors.tempRange40 && <p className="mt-1 text-sm text-red-600">{validationErrors.tempRange40}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Commodity</Label>
            <SearchableSelect
              options={commodityOptions}
              value={formData.commodity}
              onChange={(value) => {
                setFormData({ ...formData, commodity: value || '' });
                if (value) clearValidationError?.('commodity');
              }}
              placeholder="Search or select commodity"
              allowOther={false}
              error={!!validationErrors.commodity}
            />
            {validationErrors.commodity && <p className="mt-1 text-sm text-red-600">{validationErrors.commodity}</p>}
          </div>
            <div>
              <Label>Hazardous Cargo</Label>
              <SearchableSelect
                options={HAZARDOUS_CARGO_OPTIONS}
                value={formData.isHaz ? 'yes' : 'no'}
                onChange={(value) => setFormData({ ...formData, isHaz: value === 'yes' })}
                placeholder="Search or select cargo type"
                allowOther={false}
              />
            </div>
          </div>

          {formData.isHaz && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-red-50 rounded-lg">
              <div>
                <Label>Hazard Class</Label>
                <SearchableSelect
                  options={hazClassOptions}
                  value={formData.hazClass}
                  onChange={(value) => setFormData({ ...formData, hazClass: value || '' })}
                  placeholder="Search or select hazard class"
                  allowOther={false}
                />
              </div>
              <div>
                <Label className="block mb-2">MSDS Documents (max {MAX_MSDS_FILES} files, {MAX_MSDS_FILE_SIZE_MB}MB each)</Label>
                <div className="flex items-center gap-3 flex-wrap">
                  <Input
                    ref={msdsInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    multiple
                    className="hidden"
                    onChange={handleMsdsUpload}
                  />
                  <button
                    type="button"
                    onClick={() => msdsInputRef.current?.click()}
                    disabled={uploadingMsds || (formData.msdsFiles || []).length >= MAX_MSDS_FILES}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploadingMsds ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {uploadingMsds ? 'Uploading...' : ((formData.msdsFiles || []).length >= MAX_MSDS_FILES ? 'Max files reached' : 'Upload MSDS')}
                  </button>
                  <span className="text-xs text-gray-500">
                    Upload up to {MAX_MSDS_FILES} documents. Accepted: PDF, DOC, DOCX.
                  </span>
                  <span className="text-xs font-medium text-gray-700">
                    {(formData.msdsFiles || []).length} / {MAX_MSDS_FILES} files selected
                  </span>
                </div>
                {(formData.msdsFiles || []).length > 0 && (
                  <div className="mt-3 space-y-2">
                    {(formData.msdsFiles || []).map((file, index) => (
                      <div key={`${file.url || file.name}-${index}`} className="flex items-center justify-between gap-3 rounded-md border bg-white px-3 py-2">
                        <a
                          href={file.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm text-blue-600 hover:underline truncate"
                        >
                          {file.name || `MSDS ${index + 1}`}
                        </a>
                        <button
                          type="button"
                          onClick={() => removeMsdsFile(index)}
                          className="text-gray-500 hover:text-red-600"
                          aria-label={`Remove ${file.name || `MSDS ${index + 1}`}`}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

    </form>
  );
};

export default OceanFreightForm;
